import { vehicles } from '../../../mocks/vehicles';
import React from 'react';

interface VehicleFiltersProps {
  selectedMake: string;
  setSelectedMake: (make: string) => void;
  selectedModel: string;
  setSelectedModel: (model: string) => void;
  yearRange: { min: number; max: number };
  setYearRange: (range: { min: number; max: number }) => void;
  priceRange: { min: number; max: number };
  setPriceRange: (range: { min: number; max: number }) => void;
  selectedCondition: string;
  setSelectedCondition: (condition: string) => void;
  selectedTransmission: string;
  setSelectedTransmission: (transmission: string) => void;
  selectedDrivetrain: string;
  setSelectedDrivetrain: (drivetrain: string) => void;
  selectedFuelType: string;
  setSelectedFuelType: (fuelType: string) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  onClearFilters: () => void;
  searchLocation: string;
  setSearchLocation: (location: string) => void;
  searchRadius: number;
  setSearchRadius: (radius: number) => void;
}

// Generate unique lists for dropdowns, adding an "All …" option.
const makes = ['All Makes', ...Array.from(new Set(vehicles.map(v => v.make)))];
const models = ['All Models', ...Array.from(new Set(vehicles.map(v => v.model)))];
const conditions = ['All Conditions', 'Excellent', 'Very Good', 'Good', 'Fair'];
const transmissions = ['All Transmissions', 'Automatic', 'Manual', 'CVT'];
const drivetrains = ['All Drivetrains', 'FWD', 'RWD', 'AWD', '4WD'];
const fuelTypes = ['All Fuel Types', 'Gasoline', 'Electric', 'Plug-in Hybrid', 'Hybrid', 'Diesel'];
const categories = ['All Categories', 'Sedan', 'SUV', 'Truck', 'Wagon', 'Sports Car', 'Luxury'];

export default function VehicleFilters({
  selectedMake,
  setSelectedMake,
  selectedModel,
  setSelectedModel,
  yearRange,
  setYearRange,
  priceRange,
  setPriceRange,
  selectedCondition,
  setSelectedCondition,
  selectedTransmission,
  setSelectedTransmission,
  selectedDrivetrain,
  setSelectedDrivetrain,
  selectedFuelType,
  setSelectedFuelType,
  selectedCategory,
  setSelectedCategory,
  onClearFilters,
  searchLocation,
  setSearchLocation,
  searchRadius,
  setSearchRadius,
}: VehicleFiltersProps) {
  /** Formats a numeric price value for display in the UI. */
  const formatPrice = (value: number) => {
    if (value >= 100000) return '$100k+';
    if (value === 0) return '$0';
    return `$${(value / 1000).toFixed(0)}k`;
  };

  /** Safely parse an input string to a number with a fallback. */
  const safeParse = (value: string, fallback: number) => {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  };

  return (
    <div className="w-80 flex-shrink-0">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 sticky top-24">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Filters</h2>
          <button
            onClick={onClearFilters}
            className="text-sm text-teal-600 hover:text-teal-700 font-semibold whitespace-nowrap cursor-pointer"
          >
            Clear All
          </button>
        </div>

        <div className="space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
          {/* Location & Distance Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              <i className="ri-map-pin-line mr-2"></i>
              Location & Distance
            </label>
            <div className="space-y-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter city or ZIP code"
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 pr-10"
                />
                <i className="ri-search-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Within</span>
                  <span className="text-sm font-semibold text-teal-600">
                    {searchRadius === 1000 ? 'Any Distance' : `${searchRadius} miles`}
                  </span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="1000"
                  step="10"
                  value={searchRadius}
                  onChange={(e) => setSearchRadius(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>10 mi</span>
                  <span>500 mi</span>
                  <span>Any</span>
                </div>
              </div>

              {searchLocation && (
                <button
                  onClick={() => setSearchLocation('')}
                  className="w-full px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-close-line mr-1"></i>
                  Clear Location
                </button>
              )}
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              <i className="ri-car-line mr-2"></i>
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 cursor-pointer"
            >
              <option>All Categories</option>
              <option>Sedan</option>
              <option>SUV</option>
              <option>Truck</option>
              <option>Luxury</option>
              <option>Wagon</option>
            </select>
          </div>

          {/* Make Filter */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-3">Make</label>
            <select
              value={selectedMake}
              onChange={e => setSelectedMake(e.target.value)}
              className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 cursor-pointer bg-white"
            >
              {makes.map(make => (
                <option key={make} value={make}>
                  {make}
                </option>
              ))}
            </select>
          </div>

          {/* Model Filter */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-3">Model</label>
            <select
              value={selectedModel}
              onChange={e => setSelectedModel(e.target.value)}
              className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 cursor-pointer bg-white"
            >
              {models.map(model => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          </div>

          {/* Year Range Filter */}
          <div className="mb-6 pt-6 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-900 mb-3">Year Range</label>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-600">Min: {yearRange.min}</span>
                  <span className="text-xs text-gray-600">Max: {yearRange.max}</span>
                </div>
                <input
                  type="range"
                  min="2015"
                  max="2024"
                  value={yearRange.min}
                  onChange={e =>
                    setYearRange({ ...yearRange, min: safeParse(e.target.value, yearRange.min) })
                  }
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-500"
                />
                <input
                  type="range"
                  min="2015"
                  max="2024"
                  value={yearRange.max}
                  onChange={e =>
                    setYearRange({ ...yearRange, max: safeParse(e.target.value, yearRange.max) })
                  }
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-500 mt-2"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  min="2015"
                  max="2024"
                  value={yearRange.min}
                  onChange={e =>
                    setYearRange({
                      ...yearRange,
                      min: safeParse(e.target.value, 2015),
                    })
                  }
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                  placeholder="Min"
                />
                <input
                  type="number"
                  min="2015"
                  max="2024"
                  value={yearRange.max}
                  onChange={e =>
                    setYearRange({
                      ...yearRange,
                      max: safeParse(e.target.value, 2024),
                    })
                  }
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                  placeholder="Max"
                />
              </div>
            </div>
          </div>

          {/* Price Range Filter */}
          <div className="mb-6 pt-6 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-900 mb-3">Price Range</label>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-600">{formatPrice(priceRange.min)}</span>
                  <span className="text-xs text-gray-600">{formatPrice(priceRange.max)}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000"
                  step="5000"
                  value={priceRange.min}
                  onChange={e =>
                    setPriceRange({ ...priceRange, min: safeParse(e.target.value, priceRange.min) })
                  }
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-500"
                />
                <input
                  type="range"
                  min="0"
                  max="100000"
                  step="5000"
                  value={priceRange.max}
                  onChange={e =>
                    setPriceRange({ ...priceRange, max: safeParse(e.target.value, priceRange.max) })
                  }
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-500 mt-2"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    step="1000"
                    value={priceRange.min}
                    onChange={e =>
                      setPriceRange({
                        ...priceRange,
                        min: safeParse(e.target.value, 0),
                      })
                    }
                    className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    placeholder="Min"
                  />
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    step="1000"
                    value={priceRange.max}
                    onChange={e =>
                      setPriceRange({
                        ...priceRange,
                        max: safeParse(e.target.value, 100000),
                      })
                    }
                    className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    placeholder="Max"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Fuel Type Filter */}
          <div className="mb-6 pt-6 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-900 mb-3">Fuel Type</label>
            <div className="space-y-2">
              {fuelTypes.map(fuel => (
                <label key={fuel} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="fuelType"
                    checked={selectedFuelType === fuel}
                    onChange={() => setSelectedFuelType(fuel)}
                    className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">{fuel}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Condition Filter */}
          <div className="mb-6 pt-6 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-900 mb-3">Condition</label>
            <div className="space-y-2">
              {conditions.map(cond => (
                <label key={cond} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="condition"
                    checked={selectedCondition === cond}
                    onChange={() => setSelectedCondition(cond)}
                    className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">{cond}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Transmission Filter */}
          <div className="mb-6 pt-6 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-900 mb-3">Transmission</label>
            <div className="space-y-2">
              {transmissions.map(trans => (
                <label key={trans} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="transmission"
                    checked={selectedTransmission === trans}
                    onChange={() => setSelectedTransmission(trans)}
                    className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">{trans}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Drivetrain Filter */}
          <div className="pt-6 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-900 mb-3">Drivetrain</label>
            <div className="space-y-2">
              {drivetrains.map(drive => (
                <label key={drive} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="drivetrain"
                    checked={selectedDrivetrain === drive}
                    onChange={() => setSelectedDrivetrain(drive)}
                    className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">{drive}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
